import React, { useState, useEffect, useRef } from 'react';
import {
  Card,
  Typography,
  Spin,
  message,
  Radio,
  Tag,
  Alert,
  Select,
} from 'antd';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import 'leaflet.heat';
import apiService from '../../services/api';
import { useChannels } from '../../hooks/useChannels';

const { Title, Text } = Typography;
const { Option } = Select;
const CURRENT_YEAR = new Date().getFullYear();
const YEAR_OPTIONS = Array.from({ length: 5 }, (_, i) => CURRENT_YEAR - i);

const ENGLAND_CENTER = [52.8, -1.5];
const ENGLAND_ZOOM = 7;
const POSTCODES_IO_BATCH = 'https://api.postcodes.io/postcodes';

async function geocodePostcodes(postcodes) {
  const results = {};
  const chunks = [];
  for (let i = 0; i < postcodes.length; i += 100) {
    chunks.push(postcodes.slice(i, i + 100));
  }
  for (const chunk of chunks) {
    try {
      const res = await fetch(POSTCODES_IO_BATCH, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ postcodes: chunk }),
      });
      const json = await res.json();
      if (json.result) {
        json.result.forEach(({ query, result }) => {
          if (result) {
            results[query.toUpperCase()] = [result.latitude, result.longitude];
          }
        });
      }
    } catch {
      // skip failed chunk
    }
  }
  return results;
}

const JobMap = () => {
  const mapRef = useRef(null);
  const leafletMap = useRef(null);
  const heatLayer = useRef(null);
  const coordCache = useRef(null); // postcode → [lat, lng], populated once

  const [loading, setLoading] = useState(true);
  const [postcodeData, setPostcodeData] = useState(null);
  const [mode, setMode] = useState('both');
  const [year, setYear] = useState(CURRENT_YEAR);
  const [channel, setChannel] = useState('');
  const { channels } = useChannels({ activeOnly: true });
  const [stats, setStats] = useState({ collection: 0, delivery: 0, unique: 0 });
  const [geocodeError, setGeocodeError] = useState(false);

  useEffect(() => {
    setLoading(true);
    coordCache.current = null;
    if (heatLayer.current && leafletMap.current) {
      leafletMap.current.removeLayer(heatLayer.current);
      heatLayer.current = null;
    }
    const params = { year };
    if (channel) params.channel = channel;
    apiService.reports.getPostcodeMap(params)
      .then(res => setPostcodeData(res.data))
      .catch(() => message.error('Failed to load postcode data'))
      .finally(() => setLoading(false));
  }, [year, channel]);

  // Initialise map once
  useEffect(() => {
    if (!mapRef.current || leafletMap.current) return;
    leafletMap.current = L.map(mapRef.current).setView(ENGLAND_CENTER, ENGLAND_ZOOM);
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap contributors',
    }).addTo(leafletMap.current);
  }, []);

  // Geocode all postcodes once when data arrives, then cache
  useEffect(() => {
    if (!postcodeData || !leafletMap.current) return;

    const colUnique = new Set(postcodeData.collection.map(d => d.postcode));
    const delUnique = new Set(postcodeData.delivery.map(d => d.postcode));
    const allUnique = new Set([...colUnique, ...delUnique]);

    setStats({
      collection: postcodeData.collection.reduce((s, d) => s + d.count, 0),
      delivery: postcodeData.delivery.reduce((s, d) => s + d.count, 0),
      unique: allUnique.size,
    });

    const allPostcodes = [...allUnique];
    geocodePostcodes(allPostcodes).then(coords => {
      coordCache.current = coords;
      applyHeatmap(postcodeData, coords, mode);
    });
  }, [postcodeData]); // eslint-disable-line react-hooks/exhaustive-deps

  // Swap heatmap when mode changes (coords already cached)
  useEffect(() => {
    if (!coordCache.current || !leafletMap.current || !postcodeData) return;
    applyHeatmap(postcodeData, coordCache.current, mode);
  }, [mode]); // eslint-disable-line react-hooks/exhaustive-deps

  function applyHeatmap(data, coords, currentMode) {
    const combined = {};
    if (currentMode === 'collection' || currentMode === 'both') {
      data.collection.forEach(({ postcode, count }) => {
        combined[postcode] = (combined[postcode] || 0) + count;
      });
    }
    if (currentMode === 'delivery' || currentMode === 'both') {
      data.delivery.forEach(({ postcode, count }) => {
        combined[postcode] = (combined[postcode] || 0) + count;
      });
    }

    const points = Object.keys(combined)
      .filter(pc => coords[pc])
      .map(pc => [...coords[pc], combined[pc]]);

    if (points.length === 0) {
      setGeocodeError(true);
      return;
    }
    setGeocodeError(false);

    if (heatLayer.current) {
      leafletMap.current.removeLayer(heatLayer.current);
    }
    heatLayer.current = L.heatLayer(points, {
      radius: 18,
      blur: 12,
      maxZoom: 12,
      gradient: { 0.2: '#3388ff', 0.5: '#ff9900', 1.0: '#ff0000' },
    }).addTo(leafletMap.current);
  }

  return (
    <div style={{ padding: '24px' }}>
      <Title level={2} style={{ marginBottom: 4 }}>Job Map</Title>
      <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
        Heatmap of job locations by postcode
      </Text>

      {/* Stats + controls — hidden while loading */}
      {!loading && (
        <div style={{ display: 'flex', gap: 16, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
          <Tag color="blue">Collections: {stats.collection}</Tag>
          <Tag color="orange">Deliveries: {stats.delivery}</Tag>
          <Tag color="green">Unique postcodes: {stats.unique}</Tag>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 12, alignItems: 'center' }}>
            <Select value={year} onChange={setYear} style={{ width: 90 }}>
              {YEAR_OPTIONS.map(y => <Option key={y} value={y}>{y}</Option>)}
            </Select>
            <Select
              value={channel}
              onChange={setChannel}
              style={{ width: 160 }}
              placeholder="All Channels"
            >
              <Option value="">All Channels</Option>
              {channels.map(c => <Option key={c._id} value={c.name}>{c.name}</Option>)}
            </Select>
            <Radio.Group
              value={mode}
              onChange={e => setMode(e.target.value)}
              buttonStyle="solid"
            >
              <Radio.Button value="collection">Collections</Radio.Button>
              <Radio.Button value="delivery">Deliveries</Radio.Button>
              <Radio.Button value="both">Both</Radio.Button>
            </Radio.Group>
          </div>
        </div>
      )}

      {geocodeError && !loading && (
        <Alert
          type="warning"
          message="Could not geocode any postcodes. Check your internet connection or postcode formats."
          style={{ marginBottom: 12 }}
          showIcon
        />
      )}

      {/* Map div is always mounted so Leaflet can initialise on first render */}
      <Card styles={{ body: { padding: 0, overflow: 'hidden', borderRadius: 8 } }}>
        <Spin spinning={loading} size="large" style={{ minHeight: '72vh' }}>
          <div ref={mapRef} style={{ height: '72vh', width: '100%' }} />
        </Spin>
      </Card>

      {!loading && (
        <Text type="secondary" style={{ display: 'block', marginTop: 8, fontSize: 12 }}>
          Map data © OpenStreetMap contributors. Geocoding by postcodes.io.
        </Text>
      )}
    </div>
  );
};

export default JobMap;
